import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import availabilityStatusTenantSettingsObject from '@/entities/availabilityStatusTenantSettings/interface'

import {
  ALL_AVAILABILITY_STATUS_QUERY,
  SINGLE_AVAILABILITY_STATUS_QUERY,
  AVAILABILITY_STATUS_DEPENDENTS_QUERY,
  CREATE_AVAILABILITY_STATUS_MUTATION,
  UPDATE_AVAILABILITY_STATUS_MUTATION,
  DELETE_AVAILABILITY_STATUS_MUTATION,
  FORCE_DELETE_AVAILABILITY_STATUS_MUTATION,
  ADD_AVAILABILITY_STATUS_TENANT_SETTINGS_MUTATION,
  REMOVE_AVAILABILITY_STATUS_TENANT_SETTINGS_MUTATION,
  CREATE_AVAILABILITY_STATUS_TENANT_SETTINGS_MUTATION,
  DELETE_AVAILABILITY_STATUS_TENANT_SETTINGS_MUTATION,
  UPDATE_AVAILABILITY_STATUS_TENANT_SETTINGS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const travelAvailabilityStatusRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_AVAILABILITY_STATUS_QUERY, variables).then((data: any) => {
      return hydrateObject(data.availabilityStatus)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_AVAILABILITY_STATUS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.availabilityStatus)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_AVAILABILITY_STATUS_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.availabilityStatus),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_AVAILABILITY_STATUS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.availabilityStatus),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(AVAILABILITY_STATUS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([AVAILABILITY_STATUS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_AVAILABILITY_STATUS_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.availabilityStatus)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_AVAILABILITY_STATUS_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.availabilityStatus)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_AVAILABILITY_STATUS_MUTATION, variables).then((data: any) => {
      return data.deleteTravelAvailabilityStatus
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_AVAILABILITY_STATUS_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTravelAvailabilityStatus
    })
  },

  assignTenantSettings: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_AVAILABILITY_STATUS_TENANT_SETTINGS_MUTATION, variables)
  },

  unassignTenantSettings: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_AVAILABILITY_STATUS_TENANT_SETTINGS_MUTATION, variables)
  },

  createTenantSettings: async (id: string, items: Array<availabilityStatusTenantSettingsObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettings: items,
      },
    }
    return graphQLClient.request(CREATE_AVAILABILITY_STATUS_TENANT_SETTINGS_MUTATION, variables)
  },

  deleteTenantSettings: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_AVAILABILITY_STATUS_TENANT_SETTINGS_MUTATION, variables)
  },

  updateTenantSettingsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_AVAILABILITY_STATUS_TENANT_SETTINGS_ORDER_MUTATION, variables)
  },
}

export default travelAvailabilityStatusRepository
